import AxiosClient from './AxiosClient';
import { handleSuccess } from './HttpUtil';
import ApiUrl from '../services/APIUrl';
import { IReadFeatures, IReadMarketAssociations, IReadMarkets } from '../../types/IRequestTypes';
import { EErrorCode } from '../data/Constants';

import type { ICreateRuleRequest, IEffectivityRequest, IRule } from '../../types';
import type { IApiError } from '../../types/IApiTypes';
import { t } from 'i18next';

export const ProcosApi = {
  //This method returns the work item by workitem id
  getWorkItem: (workItemId: string | number) => {
    const idNum = typeof workItemId === 'number' ? workItemId : Number(workItemId);
    if (Number.isNaN(idNum) || idNum < 1) {
      return Promise.reject(new Error(t('error.invalidWorkItemId')));
    }

    return AxiosClient.get(`${ApiUrl.ReadWIs}/${workItemId}`)
      .then((response) => {
        return handleSuccess(response);
      })
      .catch((err) => {
        return { error: err };
      });
  },

  //This method returns the work item list
  getWorkItems: () => {
    return AxiosClient.get(ApiUrl.ReadWIs)
      .then((response) => {
        return handleSuccess(response);
      })
      .catch((err) => {
        return { error: err };
      });
  },

  //This method returns the Market list for the Product model
  getMarkets: (action: IReadMarkets) => {
    return AxiosClient.post(ApiUrl.ReadMarkets, {
      workItemId: action.workItemId ? action.workItemId.toString() : null,
    })
      .then((response) => {
        const total = JSON.parse(response.headers['x-pagination']).total;
        return {
          data: response.data,
          page: action.page,
          limit: action.limit,
          totalRecords: total,
        };
      })
      .catch((err) => {
        return {
          error: err,
        };
      });
  },

  //This method returns the Market association data  for the Product model
  getMarketAssociations: (action: IReadMarketAssociations) => {
    return AxiosClient.post(ApiUrl.ReadMarketAssociation, action)
      .then((response) => {
        return handleSuccess(response);
      })
      .catch((err) => {
        return { error: err };
      });
  },

  //This method makes an API call to get all Features
  getFeatures: (action: IReadFeatures) => {
    return AxiosClient.post(ApiUrl.ReadFamilyFeature, {
      workItemId: action.workItemNum.toString(),
      code: '',
      includeScopeFeatures: action.includeScopeFeatures,
      productCode: action.productCode,
    })
      .then((response) => {
        const total = JSON.parse(response.headers['x-pagination']).total;
        return {
          data: response.data,
          page: action.page,
          limit: action.limit,
          totalRecords: total,
        };
      })
      .catch((err) => {
        return { error: err };
      });
  },

  //This method calls API /ace/v1/wi/create? to creates new work item
  createWorkItem: (productCode: string) => {
    return AxiosClient.post(ApiUrl.CreateWorkItem, { productCode: productCode })
      .then((response) => {
        return handleSuccess(response);
      })
      .catch((err) => {
        return { error: err };
      });
  },

  //This method calls API /ace/productmodel/v1/rules/create to save/update/delete rules
  createRules: (action: ICreateRuleRequest) => {
    return AxiosClient.post(ApiUrl.CreateRules, action.request, { headers: { skipLoader: true } })
      .then((response) => {
        return handleSuccess(response);
      })
      .catch((e: IApiError) => {
        //Get all the feature codes from the api request
        const failedRules = action.request.marketRules.map((rule: IRule) => rule.code);
        const failed = { rules: failedRules, properties: failedRules };

        //Return failed features  if api failed by Network error other error codes
        //Return errorId true if api failed by Network error
        if (e.code === EErrorCode.NetworkError) {
          return handleSuccess({
            data: {
              isSuccess: false,
              errorId: EErrorCode.NetworkError,
              failed: failed,
            },
          });
        }
        //Return response with erroId if api failed by invalid workitem
        else if (e?.response?.data?.errorId) {
          return handleSuccess({
            data: { isSuccess: false, errorId: e?.response?.data?.errorId, failed: failed },
          });
        } else {
          return handleSuccess({
            data: { isSuccess: false, failed: failed },
          });
        }
      });
  },

  //This method calls the API /ace/productmodel/v1/effectivity/save to update effectivity
  createEffectivity: (action: IEffectivityRequest) => {
    return AxiosClient.post(ApiUrl.CreateEffectivity, action)
      .then((response) => {
        return handleSuccess(response);
      })
      .catch((e: IApiError) => {
        if (e?.response?.data?.errorId) {
          return handleSuccess({
            data: { code: e?.response?.data?.errorId },
          });
        }
        return handleSuccess({ data: { AllEffectivityFailed: 'Effectivity Update failed' } });
      });
  },
};
